import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import ModalOverlay from '../ui/ModalOverlay';
import AudioModelPicker from '../audio/AudioModelPicker';
import {
  billableCharUnits,
  defaultAudioModel,
  isSoundKind,
  pickAudioModel,
  quoteAudioCredits,
  type AudioModelKind,
} from '../../lib/audioModelCatalog';

export type EditorVoiceSourceTrack = {
  id: string;
  name: string;
  sourceUrl: string;
  kind?: string;
};

export type EditorVoiceValue = {
  voiceModelId: string;
  voiceId: string;
  text: string;
  kind: AudioModelKind;
  soundLoop?: boolean;
  sourceAudioUrl?: string;
};

type Props = {
  open: boolean;
  prompt?: string;
  kind?: AudioModelKind;
  sourceTracks?: EditorVoiceSourceTrack[];
  onConfirm: (value: EditorVoiceValue) => void;
  onClose: () => void;
};

export default function EditorVoiceModal({
  open,
  prompt,
  kind = 'voice',
  sourceTracks = [],
  onConfirm,
  onClose,
}: Props) {
  const [voiceModelId, setVoiceModelId] = useState(defaultAudioModel(kind).id);
  const [voiceId, setVoiceId] = useState(defaultAudioModel(kind).defaultVoice);
  const [text, setText] = useState(prompt || '');
  const [soundLoop, setSoundLoop] = useState(false);
  const [sourceAudioUrl, setSourceAudioUrl] = useState('');
  const firstSourceUrl = sourceTracks[0]?.sourceUrl || '';

  useEffect(() => {
    if (!open) return;
    const next = defaultAudioModel(kind);
    setVoiceModelId(next.id);
    setVoiceId(next.defaultVoice);
    setText(prompt || '');
    setSoundLoop(false);
    setSourceAudioUrl(firstSourceUrl);
  }, [open, prompt, kind, firstSourceUrl]);

  const model = pickAudioModel(voiceModelId, kind);
  const credits = useMemo(() => quoteAudioCredits(voiceModelId, text || ' '), [voiceModelId, text]);
  const { chars } = billableCharUnits(text || ' ', model.maxChars);
  const soundMode = isSoundKind(kind) || isSoundKind(model.kind);
  const needsSource = Boolean(model.requiresSourceAudio);
  const canLoop = model.poyoModel === 'generate-sounds';
  const confirmKind: AudioModelKind = soundMode ? (model.kind === 'music' ? 'music' : 'sfx') : 'voice';
  const canConfirm = Boolean(text.trim()) && (!needsSource || /^https?:\/\//i.test(sourceAudioUrl));

  if (!open) return null;

  return (
    <ModalOverlay onClose={onClose} closeOnBackdrop={false}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 440, width: 'min(440px, calc(100vw - 32px))' }}
      >
        <div className="modal-header">
          <h2 style={{ fontSize: '1.05rem' }}>
            {kind === 'music' ? 'Modelo de música' : kind === 'sfx' ? 'Modelo de som' : 'Modelo de voz'}
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: '0.78rem', color: 'rgba(255,255,255,0.62)', lineHeight: 1.45 }}>
          {kind === 'music'
            ? 'Trilha PoYo (Generate Music, MiniMax, Add Instrumental). Preço por geração — o chat (tokens) é separado.'
            : kind === 'sfx'
              ? 'Efeitos e ambiente (Generate Sounds). Preço por geração — o chat (tokens) é separado.'
              : 'Voz humana (TTS PoYo). Preço por 1.000 caracteres — o chat (tokens) é separado. Amostra oficial, sem créditos.'}
        </p>
        <AudioModelPicker
          kind={kind}
          value={{ voiceModelId, voiceId }}
          onChange={(next) => {
            setVoiceModelId(next.voiceModelId);
            setVoiceId(next.voiceId);
          }}
        />
        {needsSource && (
          <label style={{ display: 'block', marginBottom: 10 }}>
            <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
              Áudio de origem (voz ou faixa na timeline)
            </div>
            {sourceTracks.length === 0 ? (
              <div style={{ fontSize: '0.72rem', color: '#fca5a5' }}>
                Coloque uma voz ou áudio na timeline primeiro. Add Instrumental gera o acompanhamento em cima dessa faixa.
              </div>
            ) : (
              <select
                value={sourceAudioUrl}
                onChange={(e) => setSourceAudioUrl(e.target.value)}
                style={{
                  width: '100%',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: 'rgba(0,0,0,0.28)',
                  color: '#fff',
                  fontSize: '0.78rem',
                  padding: '8px 10px',
                  fontFamily: 'inherit',
                }}
              >
                {sourceTracks.map((track) => (
                  <option key={track.id} value={track.sourceUrl}>
                    {track.name || 'Faixa'}
                  </option>
                ))}
              </select>
            )}
          </label>
        )}
        {canLoop && (
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={soundLoop}
              onChange={(e) => setSoundLoop(e.target.checked)}
            />
            Loop (som contínuo)
          </label>
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            needsSource
              ? 'Estilo do instrumental (piano, cinematic, lo-fi…)…'
              : soundMode
                ? 'Descreva o som ou a trilha (chuva, beat, piano…)…'
                : 'Texto da locução…'
          }
          rows={5}
          style={{
            width: '100%',
            resize: 'vertical',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,0,0,0.28)',
            color: '#fff',
            fontSize: '0.8rem',
            padding: 10,
            fontFamily: 'inherit',
          }}
        />
        <div style={{ marginTop: 8, fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)' }}>
          {soundMode
            ? `${credits} créditos / geração · até ${model.maxChars} caracteres`
            : `${chars} caracteres · ${credits} créditos`}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
              background: 'transparent', color: 'rgba(255,255,255,0.7)',
              fontSize: '0.75rem', padding: '8px 12px', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Agora não
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={() => onConfirm({
              voiceModelId,
              voiceId,
              text: text.trim(),
              kind: confirmKind,
              soundLoop: canLoop ? soundLoop : undefined,
              sourceAudioUrl: needsSource ? sourceAudioUrl : undefined,
            })}
            style={{
              border: 'none', borderRadius: 8, background: canConfirm ? '#6366f1' : 'rgba(255,255,255,0.08)',
              color: '#fff', fontSize: '0.75rem', padding: '8px 14px',
              cursor: canConfirm ? 'pointer' : 'default', fontFamily: 'inherit', fontWeight: 600,
            }}
          >
            {confirmKind === 'music' ? 'Gerar música' : soundMode ? 'Gerar som' : 'Gerar voz'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
